import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Space,
  message,
  Divider,
  Alert,
  Typography,
  Spin,
  Select,
  Popconfirm,
} from 'antd';
import { SaveOutlined, ReloadOutlined, LockOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../../services/apiService';

const { Title, Text, Paragraph } = Typography;

interface SFConfig {
  id?: string;
  accountType?: 'standard' | 'b2b';
  clientCode: string;
  checkwordSandbox?: string;
  checkwordProd?: string;
  custId?: string;
  templateCode?: string;
  isProduction: boolean;
  isActive: boolean;
  displayName?: string;
  hasCheckwordSandbox?: boolean;
  hasCheckwordProd?: boolean;
  linkedCustomers?: Array<{ id: string; name: string }>;
}

/**
 * SF Express Configuration Settings Page (multi-config)
 * - URL ?configId=xxx → edit existing config
 * - URL ?new=1&accountType=standard|b2b → create new
 * - URL with no params → legacy: edit/create the company-default standard config
 */
const SFExpressSettings: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const configId = searchParams.get('configId') || undefined;
  const isNewMode = searchParams.get('new') === '1';
  const initialAccountType = (searchParams.get('accountType') || 'standard') as 'standard' | 'b2b';

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [config, setConfig] = useState<SFConfig | null>(null);
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    loadCustomers();
    if (configId) {
      loadConfigById(configId);
    } else if (isNewMode) {
      setConfig(null);
      form.setFieldsValue({
        accountType: initialAccountType,
        isProduction: false,
        isActive: true,
        displayName: '',
        linkedCustomerIds: [],
      });
      setFetching(false);
    } else {
      loadLegacyConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId, isNewMode]);

  const loadCustomers = async () => {
    try {
      const res: any = await apiService.getCustomersList();
      if (res?.success && Array.isArray(res.data)) {
        setCustomers(res.data.map((c: any) => ({ id: c.id, name: c.name })));
      } else if (Array.isArray(res?.data?.customers)) {
        setCustomers(res.data.customers.map((c: any) => ({ id: c.id, name: c.name })));
      }
    } catch (e) {
      console.error('Failed to load customers:', e);
    }
  };

  const loadConfigById = async (id: string) => {
    setFetching(true);
    try {
      const res: any = await apiService.getSFExpressConfigById(id);
      if (res?.success && res.data) {
        setConfig(res.data);
        form.setFieldsValue({
          accountType: res.data.accountType || 'standard',
          clientCode: res.data.clientCode,
          custId: res.data.custId,
          templateCode: res.data.templateCode,
          isProduction: res.data.isProduction,
          isActive: res.data.isActive,
          displayName: res.data.displayName || '',
          linkedCustomerIds: (res.data.linkedCustomers || []).map((c: any) => c.id),
        });
      } else {
        message.error('Configuration not found');
      }
    } catch (error: any) {
      console.error('Error loading config by id:', error);
      message.error('Failed to load configuration');
    } finally {
      setFetching(false);
    }
  };

  const loadLegacyConfig = async () => {
    setFetching(true);
    try {
      const response: any = await apiService.get('/sf-express/config');
      if (response && response.success && response.data) {
        const configData = response.data;
        setConfig(configData);
        form.setFieldsValue({
          accountType: configData.accountType || 'standard',
          clientCode: configData.clientCode,
          custId: configData.custId,
          templateCode: configData.templateCode,
          isProduction: configData.isProduction,
          isActive: configData.isActive,
        });
      } else {
        setConfig(null);
      }
    } catch (error: any) {
      console.error('Error loading SF Express config:', error);
      if (error.message && !error.message.includes('404')) {
        message.error('Failed to load configuration');
      }
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      const payload = {
        accountType: values.accountType || 'standard',
        clientCode: values.clientCode,
        checkwordSandbox: values.checkwordSandbox,
        checkwordProd: values.checkwordProd,
        custId: values.custId,
        templateCode: values.templateCode,
        isProduction: values.isProduction,
        isActive: values.isActive,
        displayName: values.displayName,
        linkedCustomerIds: values.linkedCustomerIds || [],
      };

      let response: any;
      if (configId) {
        response = await apiService.updateSFExpressConfigById(configId, payload);
      } else if (isNewMode) {
        response = await apiService.createSFExpressConfig(payload);
        if (response?.success && response?.data?.id) {
          message.success('Configuration created');
          navigate(`/settings/sf-express?configId=${response.data.id}`, { replace: true });
          return;
        }
      } else {
        response = await apiService.post('/sf-express/config', payload);
      }

      if (response && response.success) {
        message.success('SF Express configuration saved');
        if (configId) loadConfigById(configId);
      } else {
        message.error(response?.error || response?.message || 'Failed to save configuration');
      }
    } catch (error: any) {
      console.error('Error saving SF Express config:', error);
      message.error(error.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!configId) return;
    setLoading(true);
    try {
      const res: any = await apiService.deleteSFExpressConfigById(configId);
      if (res?.success) {
        message.success('Configuration deleted');
        navigate('/settings');
      } else {
        message.error(res?.message || res?.error || 'Failed to delete');
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to delete');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Loading SF Express settings..." />
      </div>
    );
  }

  const titleText = configId
    ? `Edit SF Express Configuration${config?.displayName ? ` - ${config.displayName}` : ''}`
    : isNewMode
      ? `New SF Express Configuration (${initialAccountType === 'b2b' ? 'B2B' : 'Standard'})`
      : 'SF Express Integration Settings';

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <Card>
        <Title level={3}>{titleText}</Title>
        <Paragraph type="secondary">
          Configure SF Express API credentials. Link customers below to restrict this account to those customers' orders only.
        </Paragraph>

        <Alert
          message="Security Notice"
          description="API credentials are stored securely. Production checkword is required; sandbox checkword is optional for testing."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            accountType: initialAccountType,
            isProduction: false,
            isActive: true,
          }}
        >
          <Divider orientation="left">帳號類型 / Display Name</Divider>

          <Form.Item
            name="accountType"
            label="Account Type"
            rules={[{ required: true, message: '請選擇帳號類型' }]}
          >
            <Select disabled={!!configId}>
              <Select.Option value="standard">Standard (標準)</Select.Option>
              <Select.Option value="b2b">B2B</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="displayName"
            label="Display Name"
            rules={[{ required: true, message: '請輸入顯示名稱 (例如: Pacsafe / Pet Project)' }]}
            extra="此名稱將顯示在物流選擇下拉選單中,例如：「順豐速運 (標準) - Pet Project」"
          >
            <Input placeholder="例如: Pet Project" maxLength={100} size="large" />
          </Form.Item>

          <Form.Item
            name="linkedCustomerIds"
            label="Linked Customers (連結客戶)"
            extra="僅這些客戶的訂單會看到此 SF 帳號;留空表示這個帳號不限定客戶（會出現在 catch-all 列表中）"
          >
            <Select
              mode="multiple"
              placeholder="搜尋並選擇客戶"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
              size="large"
            >
              {customers.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Divider orientation="left">API Credentials</Divider>

          <Form.Item
            name="clientCode"
            label="Client Code (客户编码)"
            rules={[{ required: true, message: 'Please enter client code' }]}
            extra="Your SF Express API client code assigned by Feng Qiao platform"
          >
            <Input placeholder="Enter client code" size="large" />
          </Form.Item>

          <Form.Item
            name="custId"
            label="Monthly Account Number (月结账号)"
            extra="Optional: Monthly settlement customer ID for billing"
          >
            <Input placeholder="Enter monthly account number (optional)" size="large" />
          </Form.Item>

          <Form.Item
            name="templateCode"
            label="Waybill Template Code (面单模板编码)"
            extra={
              <span>
                Template code for waybill PDF generation. Get this from{' '}
                <a href="https://open.sf-express.com/billPrint/305090" target="_blank" rel="noopener noreferrer">
                  SF Express Print Settings
                </a>
                . Format: fm_210_standard_XXXXXX
              </span>
            }
          >
            <Input placeholder="e.g., fm_210_standard_XXXXXX" size="large" />
          </Form.Item>

          <Divider orientation="left">API Keys (Checkwords)</Divider>

          <Form.Item
            name="checkwordSandbox"
            label={
              <Space>
                <LockOutlined />
                <Text>Sandbox Checkword (沙箱密钥)</Text>
              </Space>
            }
            extra={config?.hasCheckwordSandbox
              ? "Leave empty to keep existing checkword, or enter a new one to update"
              : "Optional: API checkword for sandbox/testing environment"}
          >
            <Input.Password
              placeholder={config?.hasCheckwordSandbox ? "Leave empty to keep existing" : "Enter sandbox checkword (optional)"}
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="checkwordProd"
            label={
              <Space>
                <LockOutlined />
                <Text>Production Checkword (生产环境密钥)</Text>
              </Space>
            }
            rules={[{ required: !config && !configId, message: 'Please enter production checkword' }]}
            extra={config?.hasCheckwordProd
              ? "Leave empty to keep existing checkword, or enter a new one to update"
              : "Required: API checkword for production environment"}
          >
            <Input.Password
              placeholder={config?.hasCheckwordProd ? "Leave empty to keep existing" : "Enter production checkword"}
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Divider orientation="left">Settings</Divider>

          <Form.Item
            name="isProduction"
            label="Production Mode"
            valuePropName="checked"
            extra="Enable production mode to use real SF Express API. Disable for testing with sandbox."
          >
            <Switch checkedChildren="Production" unCheckedChildren="Sandbox" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Enable SF Express Integration"
            valuePropName="checked"
            extra="Enable or disable this configuration"
          >
            <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
          </Form.Item>

          <Divider />

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
                size="large"
              >
                Save Configuration
              </Button>
              {configId && (
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => loadConfigById(configId)}
                  disabled={loading}
                  size="large"
                >
                  Reload
                </Button>
              )}
              {configId && (
                <Popconfirm
                  title="Delete this configuration?"
                  description="This will not affect existing shipments but will remove this carrier option from the dropdown."
                  onConfirm={handleDelete}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button icon={<DeleteOutlined />} danger disabled={loading} size="large">
                    Delete
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default SFExpressSettings;
